/**
 * A quiet ⓘ beside a heading: point at it, read what the figure means, move away.
 *
 * Nothing to open and nothing to dismiss, because the text is two sentences and the reader is in
 * the middle of looking at something else. It is a real button rather than a decorated span, so it
 * answers to the keyboard and to a tap as well as to a mouse - which is all the tooltip primitive
 * in the kit was waiting for, having sat unused since it was added.
 *
 * The label says what will be explained rather than "more information", so a screen reader user
 * hears which of the five tiles they have landed on.
 */

import { Info } from "lucide-react";

import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

export function Help({ about, text, note }: { about: string; text: string; note?: string }) {
  return (
    <Tooltip>
      <TooltipTrigger
        aria-label={`What ${about} means`}
        className="ml-1 inline-flex size-4 shrink-0 cursor-help items-center justify-center rounded-full align-middle text-muted-foreground/70 transition-colors hover:text-foreground focus-visible:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/40"
      >
        <Info aria-hidden="true" className="size-3.5" />
      </TooltipTrigger>
      {/* Wider and left-aligned: the kit's default is a one-line chip, and these are sentences.
          The role is set here because Base UI describes the trigger with `aria-describedby` and
          leaves the popup unlabelled, which is valid but leaves nothing to find it by. */}
      <TooltipContent role="tooltip" side="top" className="max-w-xs flex-col items-start gap-1 text-left">
        <p>{text}</p>
        {note && <p className="opacity-70">{note}</p>}
      </TooltipContent>
    </Tooltip>
  );
}
